
import { Shield, CheckCircle, FileCheck, BarChart3, AlertTriangle, Lock } from "lucide-react";
import { PageLayout } from "@/components/layout/PageLayout";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const NistAssessment = () => {
  return (
    <PageLayout
      title="NIST Cybersecurity Assessment"
      description="Comprehensive NIST framework assessment to evaluate and enhance your organization's security posture."
      keywords={[
        "NIST assessment",
        "cybersecurity framework",
        "security evaluation",
        "risk assessment",
        "compliance"
      ]}
      canonicalUrl="/cybersecurity/nist-assessment/"
      schema={{
        type: "service",
        data: {
          name: "NIST Cybersecurity Assessment Services",
          description: "Expert evaluation of your security program against NIST frameworks",
          provider: {
            name: "edgefi",
            sameAs: "https://edgefi.com"
          }
        }
      }}
    >
      <HeroSection
        title="Align Your Security with"
        highlighted="NIST Standards"
        description="Expert assessment of your cybersecurity program against the NIST Cybersecurity Framework to identify gaps and strengthen your security posture."
        ctaText="Schedule Assessment"
        heroPattern="grid"
      />

      <FeatureSection 
        title="Comprehensive NIST Assessment"
        subtitle="KEY FEATURES"
        description="Our NIST assessment provides a thorough evaluation of your security program across all critical domains."
        features={[
          {
            title: "Framework Alignment",
            description: "Evaluate your security controls against NIST CSF's five core functions: Identify, Protect, Detect, Respond, and Recover.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Analysis",
            description: "Detailed analysis of security gaps between your current state and NIST requirements.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Control Assessment",
            description: "In-depth evaluation of technical and administrative security controls.",
            icon: <CheckCircle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Scoring",
            description: "Quantitative and qualitative risk assessment based on NIST guidelines.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Review",
            description: "Comprehensive review of security policies, procedures, and documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Control Evaluation",
            description: "Assessment of identity and access management controls.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Assessment Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to evaluating your security program against NIST standards"
        steps={[
          {
            title: "Initial Scoping",
            description: "Define assessment boundaries and identify critical assets and systems"
          },
          {
            title: "Control Evaluation",
            description: "Assess security controls across all NIST framework categories"
          },
          {
            title: "Gap Analysis",
            description: "Identify gaps between current state and NIST requirements"
          },
          {
            title: "Risk Assessment",
            description: "Evaluate risks and prioritize remediation efforts"
          },
          {
            title: "Recommendations",
            description: "Develop detailed recommendations and roadmap"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our NIST Assessment"
        benefits={[
          {
            title: "Expert Guidance",
            description: "Work with certified NIST framework specialists"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full evaluation across all NIST CSF domains"
          },
          {
            title: "Clear Roadmap",
            description: "Actionable recommendations prioritized by risk"
          },
          {
            title: "Regulatory Alignment",
            description: "Ensure compliance with relevant standards"
          },
          {
            title: "Risk Reduction",
            description: "Identify and address security vulnerabilities"
          },
          {
            title: "Future Readiness",
            description: "Prepare for evolving security challenges"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Align with NIST Standards?"
        description="Schedule your NIST assessment today and strengthen your security posture."
        ctaText="Schedule a Consultation"
        modalTitle="Request NIST Assessment"
        modalDescription="Tell us about your organization's needs for NIST framework alignment."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "NIST Assessment",
          service_interest: "Security Assessment"
        }}
      />
    </PageLayout>
  );
};

export default NistAssessment;
